import { Select } from '@orbind/ui'
import { useRef, useState } from 'react'
import { ATRIBUTO_DA_COLUNA, iniciarArrasteDeColuna } from '../../lib/arrastar-coluna.ts'
import { rotuloDaColuna, type DefinicaoDeColuna } from './colunas-da-tabela.ts'

/**
 * As colunas de uma visão, no editor dela (SPEC-20261103-1217, SPEC-20261103-1418): quais e em que
 * ordem. Pedido do Cristian, 2026-11-03: "dentro dele seja para editar ou
 * criar, deve ter como selecionar as colunas e a ordem delas".
 *
 * `valor` ausente = o filtro não tem colunas próprias e usa as do "sem filtro"
 * — é o que vale para todo filtro criado antes disto. Ligar "colunas próprias"
 * parte das colunas que a pessoa está vendo agora (`atuais`), e não do zero:
 * quase sempre o filtro novo quer a tabela de agora com um ajuste.
 */
export function ColunasDoFiltro({
  catalogo,
  valor,
  atuais,
  onChange,
}: {
  catalogo: readonly DefinicaoDeColuna[]
  valor: readonly string[] | undefined
  /** As colunas ligadas na tela agora, sem o Título — ponto de partida. */
  atuais: readonly string[]
  onChange: (colunas: string[] | undefined) => void
}) {
  const proprias = valor !== undefined
  const lista = (valor ?? []).filter((k) => k !== 'titulo' && catalogo.some((c) => c.key === k))
  const listaRef = useRef<HTMLOListElement>(null)
  const [indo, setIndo] = useState<string | null>(null)
  const [alvo, setAlvo] = useState<string | null>(null)

  const mover = (i: number, passo: -1 | 1) => {
    const j = i + passo
    if (j < 0 || j >= lista.length) return
    const copia = [...lista]
    const [k] = copia.splice(i, 1)
    copia.splice(j, 0, k!)
    onChange(copia)
  }
  const disponiveis = catalogo
    .filter((c) => c.key !== 'titulo' && !lista.includes(c.key))
    .map((c) => ({ value: c.key, label: c.rotulo }))

  return (
    <div className="wi-fcol">
      <span className="wi-fcol__titulo">Colunas da lista</span>
      <div
        className="ob-filterjoin ob-filterjoin--display"
        role="radiogroup"
        aria-label="Colunas da lista nesta visão"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!proprias}
          className={`ob-filterjoin__opt${!proprias ? ' ob-filterjoin__opt--on' : ''}`}
          onClick={() => onChange(undefined)}
        >
          <i className="fas fa-columns" aria-hidden /> As de quem abre
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={proprias}
          className={`ob-filterjoin__opt${proprias ? ' ob-filterjoin__opt--on' : ''}`}
          onClick={() => {
            if (!proprias) onChange([...atuais])
          }}
        >
          <i className="fas fa-list-ol" aria-hidden /> Próprias desta visão
        </button>
      </div>

      {proprias ? (
        <>
          <ol className="wi-fcol__lista" ref={listaRef}>
            <li className="wi-fcol__item wi-fcol__item--fixa">
              <span className="wi-fcol__nome">Título</span>
              <em>fixa</em>
            </li>
            {lista.map((k, i) => (
              <li
                key={k}
                {...{ [ATRIBUTO_DA_COLUNA]: k }}
                className={`wi-fcol__item${indo === k ? ' wi-fcol__item--indo' : ''}${alvo === k ? ' wi-fcol__item--alvo' : ''}`}
                onPointerDown={(e) =>
                  iniciarArrasteDeColuna(e, {
                    chave: k,
                    caixa: listaRef.current,
                    ignorar: 'button',
                    onInicio: setIndo,
                    onSobre: setAlvo,
                    onFim: () => {
                      setIndo(null)
                      setAlvo(null)
                    },
                    onSoltar: (destino) => {
                      const copia = lista.filter((x) => x !== k)
                      const j = lista.indexOf(destino)
                      // como na tabela: descendo, fica depois do destino; subindo, antes
                      copia.splice(j, 0, k)
                      onChange(copia)
                    },
                  })
                }
              >
                <i className="fas fa-grip-vertical wi-fcol__alca" aria-hidden />
                <span className="wi-fcol__nome">{rotuloDaColuna(k, catalogo)}</span>
                <button
                  type="button"
                  className="wi-fcol__btn"
                  aria-label={`Subir ${rotuloDaColuna(k, catalogo)}`}
                  disabled={i === 0}
                  onClick={() => mover(i, -1)}
                >
                  <i className="fas fa-chevron-up" aria-hidden />
                </button>
                <button
                  type="button"
                  className="wi-fcol__btn"
                  aria-label={`Descer ${rotuloDaColuna(k, catalogo)}`}
                  disabled={i === lista.length - 1}
                  onClick={() => mover(i, 1)}
                >
                  <i className="fas fa-chevron-down" aria-hidden />
                </button>
                <button
                  type="button"
                  className="wi-fcol__btn"
                  aria-label={`Tirar ${rotuloDaColuna(k, catalogo)}`}
                  title="Tirar a coluna desta visão"
                  onClick={() => onChange(lista.filter((x) => x !== k))}
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </li>
            ))}
          </ol>
          {disponiveis.length > 0 ? (
            <div className="wi-fcol__add">
              <Select
                value={null}
                options={disponiveis}
                placeholder="+ Adicionar coluna"
                aria-label="Adicionar coluna à visão"
                onChange={(k) => {
                  if (k) onChange([...lista, k])
                }}
              />
            </div>
          ) : null}
        </>
      ) : (
        <p className="wi-fcol__nota">Nesta visão, cada pessoa vê as colunas que já escolheu para a lista.</p>
      )}
    </div>
  )
}
